import * as React from "react";
import { useEffect } from "react";
import { Toaster } from "@/shared/components/ui/toaster";
import { Toaster as Sonner } from "@/shared/components/ui/sonner";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SidebarProvider } from "@/shared/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Header } from "@/components/Header";
import { AIChatbot } from "@/components/AIChatbot";
import { AppLoadingGate } from "@/components/AppLoadingGate";
import { CrossTabSyncProvider } from "@/components/CrossTabSyncProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ReferralDiscountBanner } from "@/components/ReferralDiscountBanner";
import { CallNotificationModal } from "@/components/CallNotificationModal";
import { SettingsProvider } from "@/shared/contexts/SettingsContext";
import { NotificationProvider } from "@/shared/contexts/NotificationContext.new";
import { TeamMembersProvider } from "@/shared/contexts/TeamMembersContext";
import { AuthProvider, useAuth } from "@/shared/contexts/AuthContext";
import { SubscriptionProvider } from "@/features/billing/contexts/SubscriptionContext";
import { ApiHealthProvider, useApiHealth, setApiHealthListener } from "@/shared/contexts/ApiHealthContext";
import { MaintenanceScreen } from "@/components/MaintenanceScreen";
import { useSessionManager } from "@/shared/hooks/useSessionManager";
import { useCallNotifications } from "@/shared/hooks/useCallNotifications";
import { useRecordingNotifications } from "@/shared/hooks/useRecordingNotifications";
import { useTokenRefresh } from "@/shared/hooks/useTokenRefresh";
import { useMobileCheckoutReturn } from "@/shared/hooks/useMobileCheckoutReturn";
import { supabase } from "@/integrations/supabase/client";
import { initSentry } from "@/lib/sentry";
import { initMetaPixel } from "@/shared/utils/metaPixel";
import { useToast } from "@/shared/hooks/use-toast";
import { ExtensionHcpPrefillProvider } from "@/shared/hooks/useExtensionHcpPrefill";

// Initialize Sentry as early as possible
initSentry();

// Initialize Meta Pixel for conversion tracking
initMetaPixel();

// Public Pages
import LandingPage from "@/features/public/pages/LandingPage";
import AdLandingPage1 from "@/features/public/pages/AdLandingPage1";
import AdLandingPage2 from "@/features/public/pages/AdLandingPage2";
import FeaturesPage from "@/features/public/pages/FeaturesPage";
import PricingPage from "@/features/public/pages/PricingPage";
import ContactUsPage from "@/features/public/pages/ContactUsPage";
import CustomerSupportPage from "@/features/public/pages/CustomerSupportPage";
import LegalPage from "@/features/public/pages/LegalPage";
import DeleteAccountPage from "@/features/public/pages/DeleteAccountPage";

// Auth Pages
import AuthPage from "@/features/auth/pages/AuthPage";
import ProfileSetupPage from "@/features/auth/pages/ProfileSetupPage";
import OAuthCallbackPage from "@/features/auth/pages/OAuthCallbackPage";
import ExtensionAuthPage from "@/features/auth/pages/ExtensionAuthPage";
import GoogleCalendarCallbackPage from "@/features/integrations/GoogleCalendarCallbackPage";

// Core Pages (stay in src/pages)
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import ThankYouPage from "./pages/ThankYouPage";

// Feature Pages
import CallsPage from "@/features/calls/pages/CallsPage";
import ContactsPage from "@/features/contacts/pages/ContactsPage";
import ProfilePage from "@/features/profile/pages/ProfilePage";
import InvoicesPage from "@/features/invoices/pages/InvoicesPage";
import MonthlyFeesPage from "@/features/invoices/pages/MonthlyFeesPage";
import BillingPage from "@/features/billing/pages/BillingPage";
import BillingClosePage from "@/features/billing/pages/BillingClosePage";
import PaymentPage from "@/features/billing/pages/PaymentPage";
import MonthlyPaymentPage from "@/features/billing/pages/MonthlyPaymentPage";
import SubscriptionManagementPage from "@/features/billing/pages/SubscriptionManagementPage";
import SubscriptionCancelPage from "@/features/billing/pages/SubscriptionCancelPage";
import SubscriptionSuccessPage from "@/features/billing/pages/SubscriptionSuccessPage";
import SubscriptionErrorPage from "@/features/billing/pages/SubscriptionErrorPage";
import InvoiceSubscriptionSuccessPage from "@/features/invoices/pages/InvoiceSubscriptionSuccessPage";
import InvoiceSubscriptionErrorPage from "@/features/invoices/pages/InvoiceSubscriptionErrorPage";
import MerchantOnboarding from "@/features/billing/pages/MerchantOnboardingPage";
import VideoCallPage from "@/features/videocall/pages/VideoCallPage";
import PreJoinLobby from "@/features/videocall/pages/PreJoinLobby";
import VideoCallDemoPage from "@/features/videocall/pages/VideoCallDemoPage";
import RecordSubmissionPage from "@/features/videocall/pages/RecordSubmissionPage";
import PublicIntakePage from "@/features/intake/pages/PublicIntakePage";
import SessionSummaryPage from "@/features/videocall/pages/SessionSummaryPage";
import ReportsPage from "@/features/reports/pages/ReportsPage";
import SettingsPage from "@/features/settings/pages/SettingsPage";
import AppsPage from "@/features/settings/pages/AppsPage";
import AccountPausedPage from "@/features/settings/pages/AccountPausedPage";
import MessagesPage from "@/features/messages/pages/MessagesPage";
import NotificationsPage from "@/features/notifications/pages/NotificationsPage";
import TeamManagementPage from "@/features/team/pages/TeamManagementPage";
import UsersPage from "@/features/team/pages/UsersPage";
import ReferralsPage from "@/features/referrals/pages/ReferralsPage";
import ReferFriendPage from "@/features/referrals/pages/ReferFriendPage";
import SupportPage from "@/features/support/pages/SupportPage";
import CalendarPage from "./pages/CalendarPage";

// Create QueryClient with React in scope
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const locationState = location.state as {
    callData?: { isHost?: boolean };
  } | null;
  const isLandingPage = location.pathname === '/' || location.pathname.startsWith('/lp/');
  const isAuthPage = location.pathname === '/auth';
  const isOAuthCallback = location.pathname.startsWith('/oauth/') || location.pathname.startsWith('/auth/callback') || location.pathname.startsWith('/auth/google-calendar/');
  // Detect if running in WebView (mobile app)
  const isWebView = typeof (window as any).ReactNativeWebView !== 'undefined' ||
    navigator.userAgent.includes('WlkthruMobileApp') ||
    (window as any).__IS_MOBILE_WEBVIEW__ === true;

  const isVideoCallPage =
  location.pathname.startsWith('/call/') ||
  location.pathname.startsWith('/guest/') ||
  location.pathname.startsWith('/video-call') ||
  location.pathname.startsWith('/join/') ||
  location.pathname.startsWith('/record/') ||
  location.pathname.startsWith('/r/') ||
  (location.pathname === '/session-summary' && isWebView); // Session summary full-screen for WebView
  const isPaymentPage = location.pathname.startsWith('/pay/') || location.pathname.startsWith('/pay-monthly/');
  const isProfileSetup = location.pathname === '/profile-setup';
  const isThankYouPage = location.pathname === '/thank-you';
  const isSubscriptionPage = location.pathname === '/subscription-success' || location.pathname === '/subscription-error' || location.pathname === '/invoice-subscription-success' || location.pathname === '/invoice-subscription-error';
  const isExtensionAuth = location.pathname === '/extension-auth';

  const { toast } = useToast();

  // API Health monitoring - shows maintenance screen on consecutive server failures
  const { isServerDown, reportError, reportSuccess, dismissMaintenance } = useApiHealth();

  // Bridge React context to global API client (runs once on mount)
  useEffect(() => {
    setApiHealthListener({
      onError: reportError,
      onSuccess: reportSuccess,
    });
    return () => setApiHealthListener(null);
  }, [reportError, reportSuccess]);

  // Initialize session manager for daily reset functionality
  useSessionManager();
  // Mount notifications listeners (calls + guest recordings)
  useCallNotifications();
  useRecordingNotifications();
  // Enable silent token refresh (prevents session expired errors)
  useTokenRefresh();
  // Handle mobile Stripe checkout returns (same-page redirect)
  useMobileCheckoutReturn();

  // Global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[Global] Unhandled promise rejection:', event.reason);

      // Don't reload page, just show error toast
      toast({
        title: "An error occurred",
        description: "Please try again. If the issue persists, refresh the page.",
        variant: "destructive",
      });

      event.preventDefault(); // Prevent default error handling
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, [toast]);

  // Clean up any stale localStorage flags from old Stripe flow
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('trial_setup') === 'success') {
      localStorage.removeItem('pendingTrialCheckout');
      params.delete('trial_setup');
      const search = params.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // Show maintenance screen if server appears to be down
  // User can dismiss to continue using the app
  if (isServerDown) {
    return <MaintenanceScreen onRetry={dismissMaintenance} />;
  }

  // Landing page and public pages (no sidebar/header)
  // Special case: /delete-account shows without sidebar for non-authenticated users
  const isPublicDeleteAccount = location.pathname === '/delete-account' && !isAuthenticated;
  if (isLandingPage || location.pathname === '/features' || location.pathname === '/pricing' || location.pathname === '/contact' || location.pathname === '/customer-support' || location.pathname === '/legal' || location.pathname === '/billing/close' || isPublicDeleteAccount) {
    return (
      <main className="min-h-screen bg-background">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/lp/see-the-job" element={<AdLandingPage1 />} />
          <Route path="/lp/pricing" element={<AdLandingPage2 />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/customer-support" element={<CustomerSupportPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/billing/close" element={<BillingClosePage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
        </Routes>
      </main>
    );
  }

  // Full-screen pages (no sidebar/header)
  if (isAuthPage || isOAuthCallback || isExtensionAuth || isVideoCallPage || isPaymentPage || isProfileSetup || isThankYouPage || isSubscriptionPage || location.pathname === '/signup') {
    return (
      <main className="min-h-screen bg-background">
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          <Route path="/extension-auth" element={<ExtensionAuthPage />} />
          <Route path="/profile-setup" element={<ProfileSetupPage />} />
          <Route path="/pay/:invoice_id" element={<PaymentPage />} />
          <Route path="/pay-monthly/:monthly_invoice_id" element={<MonthlyPaymentPage />} />
          <Route path="/join/:roomId" element={<PreJoinLobby />} />
          <Route path="/oauth/google" element={<OAuthCallbackPage />} />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />
          <Route path="/auth/google-calendar/callback" element={<GoogleCalendarCallbackPage />} />
          <Route path="/record/:token" element={<RecordSubmissionPage />} />
          <Route path="/r/:contractorId" element={<PublicIntakePage />} />
          <Route path="/call/:roomId" element={<VideoCallPage />} />
          <Route path="/guest/:roomId" element={<VideoCallPage />} />
          <Route path="/video-call/:roomId" element={<VideoCallPage />} />
          <Route path="/video-call" element={<VideoCallDemoPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscription-error" element={<SubscriptionErrorPage />} />
          <Route path="/invoice-subscription-success" element={<InvoiceSubscriptionSuccessPage />} />
          <Route path="/invoice-subscription-error" element={<InvoiceSubscriptionErrorPage />} />
          <Route path="/session-summary" element={<SessionSummaryPage />} />
        </Routes>
      </main>
    );
  }

  return (
    <AppLoadingGate>
      <CrossTabSyncProvider>
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <div className="flex-1 flex flex-col">
              <Header />
              <ReferralDiscountBanner />
              <main className="flex-1 bg-wlkthru-light-bg transition-all duration-200">
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/calls" element={<CallsPage />} />
                  <Route path="/contacts" element={<ContactsPage />} />
                  <Route path="/calendar" element={<CalendarPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/apps" element={<AppsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  {/* Billing is now handled within ProfilePage as a tab */}
                  {/* Stripe redirects for subscription management */}
                  <Route path="/success" element={<BillingPage />} />
                  <Route path="/cancel" element={<BillingPage />} />
                  <Route path="/invoices" element={<InvoicesPage />} />
                  <Route path="/invoices/monthly-fees" element={<MonthlyFeesPage />} />
                  <Route path="/team-management" element={<TeamManagementPage />} />
                  <Route path="/manage-subscription" element={<SubscriptionManagementPage />} />
                  <Route path="/account-paused" element={<AccountPausedPage />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/referrals" element={<ReferralsPage />} />
                  <Route path="/refer" element={<ReferFriendPage />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/merchant-onboarding" element={<MerchantOnboarding />} />
                  <Route path="/session-summary" element={<SessionSummaryPage />} />
                  <Route path="/delete-account" element={<DeleteAccountPage />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
            </div>
            <AIChatbot />
            <CallNotificationModal />
          </div>
        </SidebarProvider>
      </CrossTabSyncProvider>
    </AppLoadingGate>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <SettingsProvider>
            <NotificationProvider>
              <TeamMembersProvider>
                <QueryClientProvider client={queryClient}>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <BrowserRouter>
                      <ApiHealthProvider>
                        <ExtensionHcpPrefillProvider>
                          <AppContent />
                        </ExtensionHcpPrefillProvider>
                      </ApiHealthProvider>
                    </BrowserRouter>
                  </TooltipProvider>
                </QueryClientProvider>
              </TeamMembersProvider>
            </NotificationProvider>
          </SettingsProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;